import React from 'react';
import { Check, Zap, Shield, Crown } from 'lucide-react';

const PricingPlans = ({ plans, onUpgrade, currentPlanId }) => {
  if (!plans || plans.length === 0) return null;

  return (
    <div className="mt-12">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-black text-gray-900 mb-2">Upgrade Your Experience</h2>
        <p className="text-gray-500 text-sm max-w-md mx-auto">Choose a plan that fits your organization's growth. Scalable solutions for every stage.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <PlanCard 
            key={plan._id} 
            plan={plan} 
            isCurrent={plan._id === currentPlanId}
            onUpgrade={() => onUpgrade(plan._id)}
          />
        ))}
      </div>
    </div>
  );
};

const PlanCard = ({ plan, isCurrent, onUpgrade }) => {
  const isPremium = plan.name.toLowerCase().includes('premium') || plan.name.toLowerCase().includes('enterprise');
  const isRecommended = plan.name.toLowerCase().includes('standard') || plan.name.toLowerCase().includes('pro');

  return (
    <div className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 ${
      isRecommended 
      ? 'bg-white border-2 border-primary-600 shadow-xl shadow-primary-100 scale-105 z-10' 
      : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
    }`}>
      {isRecommended && (
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
          Recommended
        </span>
      )}

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          {isPremium ? (
            <div className="p-1.5 bg-amber-50 rounded-lg">
              <Crown className="h-4 w-4 text-amber-500" />
            </div>
          ) : isRecommended ? (
            <div className="p-1.5 bg-primary-50 rounded-lg">
              <Zap className="h-4 w-4 text-primary-600" />
            </div>
          ) : (
            <div className="p-1.5 bg-emerald-50 rounded-lg">
              <Shield className="h-4 w-4 text-emerald-600" />
            </div>
          )}
          <h3 className="text-lg font-black text-gray-900">{plan.name}</h3>
        </div>
        <div className="flex items-baseline gap-1 mt-4">
          <span className="text-4xl font-black text-gray-900">₹{plan.price}</span>
          <span className="text-gray-400 text-sm font-bold">/ month</span>
        </div>
        <p className="text-gray-500 text-xs mt-3 font-medium leading-relaxed">
          {plan.description || `Unlock ${plan.messageLimit.toLocaleString()} messages per month with high priority delivery.`}
        </p>
      </div>

      <div className="space-y-4 mb-8 flex-grow">
        <FeatureItem label={`${plan.messageLimit.toLocaleString()} Messages`} />
        <FeatureItem label="Advanced Analytics" />
        <FeatureItem label="24/7 Priority Support" />
        <FeatureItem label="Multi-user Access" />
        {isPremium && <FeatureItem label="White-label Dashboard" />}
      </div>

      <button
        disabled={isCurrent}
        onClick={onUpgrade}
        className={`w-full py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all ${
          isCurrent
          ? 'bg-gray-100 text-gray-400 cursor-default'
          : isRecommended
          ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-200 active:transform active:scale-95'
          : 'bg-gray-900 text-white hover:bg-black active:transform active:scale-95'
        }`}
      >
        {isCurrent ? 'Current Plan' : 'Select Plan'}
      </button>
    </div>
  );
};

const FeatureItem = ({ label }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center">
      <Check className="h-3 w-3 text-emerald-600" />
    </div>
    <span className="text-xs font-bold text-gray-600">{label}</span>
  </div>
);

export default PricingPlans;
